<template>
  <div>
    <div v-if="quantity">
      <div
        v-if="quantityType === 'qty'"
        class="align-center d-flex flex-column-reverse vertical-chip e2e-cartitem-qty"
        :class="
          quantity ? 'background-primary-color' : 'background-color-seashell'
        "
      >
        <v-btn
          class="remove-vuetify-button-opacity-on-focus e2e-qty-minus"
          @click.stop="less"
          :disabled="!quantity"
          fab
          x-small
        >
          <v-icon>mdi-minus</v-icon>
        </v-btn>
        <div
          :class="quantity ? 'color-white' : 'color-black-magic'"
          class="font-size-sm quantity-display-width text-center"
        >
          {{ cartProduct[quantityType] }}
        </div>
        <v-btn
          class="remove-vuetify-button-opacity-on-focus e2e-qty-plus"
          fab
          x-small
          :disabled="isMoreDisabled"
          @click.stop="more"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>
      <v-chip v-else :color="quantity ? 'primary' : 'secondary'" :small="small">
        <v-btn
          class="remove-vuetify-button-opacity-on-focus e2e-qty-minus"
          :disabled="!quantity"
          :small="!small"
          :x-small="small"
          @click.stop="less"
          fab
        >
          <v-icon>mdi-minus</v-icon>
        </v-btn>
        <div
          :class="quantity ? 'color-white' : 'color-black-magic'"
          class="quantity-display-width text-center"
        >
          {{ cartProduct[quantityType] }}
        </div>
        <v-btn
          class="remove-vuetify-button-opacity-on-focus e2e-qty-plus"
          :small="!small"
          :x-small="small"
          :disabled="isMoreDisabled"
          fab
          @click.stop="more"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-chip>
    </div>
    <div v-else>
      <v-btn
        color="primary"
        :height="size.height"
        :width="size.width"
        outlined
        rounded
        :disabled="isMoreDisabled"
        :style="{ background: cartProduct.loyalty ? 'white' : '' }"
        @click.stop="more"
      >
        <span
          v-if="!redeemable && cartProduct.in_stock"
          :class="small ? 'font-size-lg' : 'font-size-xxl'"
          class="pr-2"
          >+</span
        >
        <span
          :class="redeemable ? 'px-5' : ''"
          class="font-size-sm font-weight-bold text-none text-center"
        >{{ buttonText }}</span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { getProductDetailViewFromProductState } from "@/util.js";
import debounce from "lodash.debounce";
import { mapGetters } from "vuex";

const getPsuedoID = () => Math.floor(Math.random() * 1e15);

const size = {
  small: { height: 44, width: 118 },
  large: { height: 52 },
};

export default {
  name: "CartQuantityButton",
  computed: {
    ...mapGetters("store", {
      total_points_remaining: "total_points_remaining",
      club_free_products_qty_remaining: "club_free_products_qty_remaining",
    }),
    buttonText() {
      if (!this.cartProduct.in_stock) {
        return 'Unavailable';
      }

      return this.redeemable ? 'Redeem' : 'Add to Cart';
    },
    hasExceededClubLimit() {
      return (
        this.cartProduct.club_id
        && this.club_free_products_qty_remaining(this.cartProduct.club_id) < 1
      );
    },
    hasInsufficientPoints() {
      return (
        this.total_points_remaining < this.cartProduct.redemption_points_offer
      );
    },
    isMoreDisabled() {
      if (!this.cartProduct.in_stock) {
        return true;
      }

      return (
        this.redeemable && (this.hasInsufficientPoints || this.hasExceededClubLimit)
      );
    },
    quantity() {
      return this.cartProduct[this.quantityType] > 0;
    },
    size() {
      return this.small ? size.small : size.large;
    },
    redeemable() {
      return (
        this.cartProduct.redemption_points_offer_id ||
        this.cartProduct.is_redemption
      );
    },
  },
  data() {
    /*
     *
     * little bit of magic because we need a unique instance of this debounce function on each instance
     * of this component: https://stackoverflow.com/questions/45908853/vuejs-2-debounce-on-multiple-components
     *
     */
    return {
      debouncer: debounce(function (product) {
        this.$store.dispatch("cart/updateCart", product);
      }, 1000),
    };
  },
  methods: {
    getProductDetailViewFromProductState,
    less() {
      if (this.cartProduct[this.quantityType] === 0) return;
      if (this.cartProduct.modifier_groups && this.quantityType === "cartQty") {
        this.toProductDetail();
      } else {
        const q = parseFloat(this.cartProduct[this.quantityType]);
        const qty = this.cartProduct.byweight ? q - 0.5 : q - 1;
        this.$store.commit("cart/UPDATE_NOW", { ...this.cartProduct, qty });
        this.debouncer({ ...this.cartProduct, qty });
      }
    },
    more() {
      if (this.cartProduct.modifier_groups && this.quantityType === "cartQty") {
        this.toProductDetail();
      } else {
        const q = parseFloat(this.cartProduct[this.quantityType]);
        const qty = this.cartProduct.byweight ? q + 0.5 : q + 1;

        const id = getPsuedoID();

        this.$store.commit("cart/UPDATE_NOW", {
          ...this.cartProduct,
          ...(!this.cartProduct.product_session && {
            product_session: id,
          }),
          qty,
        });

        this.debouncer({
          ...this.cartProduct,
          ...(!this.cartProduct.product_session && {
            product_session: id,
          }),
          qty,
        });
      }
    },
    toProductDetail() {
      this.$store.dispatch(
        "product/setCurrentProductDetails",
        this.cartProduct,
      );
      const view = getProductDetailViewFromProductState(this.cartProduct);
      this.$vuetify.breakpoint.xsOnly
        ? this.$router.push({
            name: "product",
            params: { upc: this.cartProduct.upc_encoded, view },
          })
        : this.$store.dispatch("product/openDialog", view);
    },
  },
  props: {
    cartProduct: Object,
    quantityType: String,
    small: Boolean,
  },
};
</script>
<style>
.remove-vuetify-button-opacity-on-focus:focus::before {
  opacity: 0 !important;
}
</style>
